import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, History, MapPin, Pencil, Plus, Trash2, TriangleAlert, Upload, Wrench } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { DocumentList } from '../../components/DocumentList';
import { DocumentUploadModal } from '../../components/DocumentUploadModal';
import { EquipmentFormModal } from '../../components/EquipmentFormModal';
import { ConditionBadge, EquipmentStatusBadge, InspectionStatusBadge, ScoreBadge } from '../../components/facilities-ui';
import { useToast } from '../../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Select,
  Table,
  Td,
  Textarea,
  Th,
  cn,
} from '../../components/ui';
import { PriorityBadge, WorkOrderStatusBadge } from '../../components/work-order-badges';
import { WorkOrderFormModal } from '../../components/WorkOrderFormModal';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useDesk } from '../../lib/desk';
import {
  describeRemainingLife,
  equipmentLabel,
  FACILITY_SYSTEM_LABEL,
  HISTORY_KIND_LABEL,
  placeLabel,
  SCHEDULE_KIND_LABEL,
  type EquipmentDetail,
  type EquipmentHistoryEntry,
  type EquipmentInput,
  type FacilityLogKind,
  type HistoryEntryInput,
} from '../../lib/facilities';
import { formatDate, formatDue, formatMinutes, formatMoney, formatUtcDate, isOverdue, toDateInput } from '../../lib/format';

type Tone = Parameters<typeof Badge>[0]['tone'];

const HISTORY_KIND_TONE: Record<FacilityLogKind, Tone> = {
  MAINTENANCE: 'green',
  REPAIR: 'amber',
  INSPECTION: 'blue',
  REPLACEMENT: 'violet',
  INSTALLATION: 'brand',
  NOTE: 'gray',
};

/** Kinds that can start the equipment's expected life over. */
const RESTARTS_LIFE: FacilityLogKind[] = ['REPLACEMENT', 'INSTALLATION'];

const years = (count: number) => `${count} year${count === 1 ? '' : 's'}`;

/** "$450" → 450; blank → null; anything else → NaN. */
function parseAmount(value: string) {
  const cleaned = value.replace(/[$,\s]/g, '');
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? Math.round(amount * 100) / 100 : Number.NaN;
}

function HistoryEntryModal({ open, onClose, equipment }: { open: boolean; onClose(): void; equipment: EquipmentDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<FacilityLogKind>('MAINTENANCE');
  const [performedOn, setPerformedOn] = useState(() => toDateInput(new Date()));
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [cost, setCost] = useState('');
  const [vendor, setVendor] = useState('');
  const [restartLife, setRestartLife] = useState(false);
  const [error, setError] = useState('');
  const today = toDateInput(new Date());

  useEffect(() => {
    if (!open) return;
    setKind('MAINTENANCE');
    setPerformedOn(toDateInput(new Date()));
    setSummary('');
    setDetails('');
    setCost('');
    setVendor('');
    setRestartLife(false);
    setError('');
  }, [open]);

  const mutation = useMutation({
    mutationFn: (input: HistoryEntryInput) => api.post(`/facilities/assets/${equipment.id}/history`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Added to the history');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const amount = parseAmount(cost);
    if (Number.isNaN(amount)) {
      setError('Enter the cost as an amount, like 450.00');
      return;
    }
    setError('');
    mutation.mutate({
      kind,
      performedOn: performedOn || undefined,
      summary: summary.trim(),
      details: details.trim() || null,
      cost: amount,
      vendor: vendor.trim() || null,
      restartLife: RESTARTS_LIFE.includes(kind) && restartLife,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a history record"
      description={equipmentLabel(equipment)}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="history-entry-form" loading={mutation.isPending}>
            Add record
          </Button>
        </>
      }
    >
      <form id="history-entry-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kind">
            <Select value={kind} onChange={(event) => setKind(event.target.value as FacilityLogKind)}>
              {(Object.keys(HISTORY_KIND_LABEL) as FacilityLogKind[]).map((value) => (
                <option key={value} value={value}>
                  {HISTORY_KIND_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Performed on" required hint="Records are for work that’s been done">
            <Input type="date" value={performedOn} max={today} required onChange={(event) => setPerformedOn(event.target.value)} />
          </Field>
        </div>
        <Field label="Summary" required>
          <Input value={summary} onChange={(event) => setSummary(event.target.value)} maxLength={200} required placeholder="e.g. Replaced the supply fan belt" />
        </Field>
        <Field label="Details">
          <Textarea value={details} onChange={(event) => setDetails(event.target.value)} maxLength={5000} rows={3} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Cost">
            <Input inputMode="decimal" value={cost} onChange={(event) => setCost(event.target.value)} placeholder="0.00" />
          </Field>
          <Field label="Vendor">
            <Input value={vendor} onChange={(event) => setVendor(event.target.value)} maxLength={150} />
          </Field>
        </div>
        {RESTARTS_LIFE.includes(kind) && (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={restartLife}
              onChange={(event) => setRestartLife(event.target.checked)}
              className="mt-0.5 size-4 rounded border-slate-300 text-brand-600"
            />
            <span>
              Start its expected life over from this date
              <span className="block text-xs text-slate-500">Sets the installed date to this day and puts the equipment back in service.</span>
            </span>
          </label>
        )}
      </form>
    </Modal>
  );
}

function LifecycleCard({ equipment, today }: { equipment: EquipmentDetail; today: string }) {
  const remaining = describeRemainingLife(equipment.remainingYears);
  const warrantyExpired = !!equipment.warrantyExpiresOn && equipment.warrantyExpiresOn < today;
  const incomplete = !equipment.installedOn || equipment.expectedLifeYears === null;
  return (
    <Card>
      <CardHeader title="Lifecycle" />
      <div className="p-5">
        <DescriptionList
          columns={1}
          items={[
            { label: 'Installed', value: equipment.installedOn && formatUtcDate(equipment.installedOn) },
            { label: 'Expected life', value: equipment.expectedLifeYears !== null ? years(equipment.expectedLifeYears) : null },
            { label: 'Age', value: equipment.ageYears !== null ? years(equipment.ageYears) : null },
            { label: 'Replacement year', value: equipment.replacementYear },
            {
              label: 'Remaining life',
              value: remaining && <span className={cn((equipment.remainingYears ?? 0) < 0 && 'font-medium text-red-700')}>{remaining}</span>,
            },
            { label: 'Purchase cost', value: equipment.purchaseCost !== null ? formatMoney(equipment.purchaseCost) : null },
            { label: 'Replacement cost', value: equipment.replacementCost !== null ? formatMoney(equipment.replacementCost) : null },
            {
              label: 'Warranty expires',
              value: equipment.warrantyExpiresOn && (
                <span className={cn(warrantyExpired && 'text-red-700')}>
                  {formatUtcDate(equipment.warrantyExpiresOn)}
                  {warrantyExpired && ' · expired'}
                </span>
              ),
            },
          ]}
        />
        {incomplete && equipment.permissions.edit && (
          <p className="mt-4 text-xs text-slate-500">Add the installed date and expected life to forecast when it’ll need replacing.</p>
        )}
      </div>
    </Card>
  );
}

function CostsCard({ costs }: { costs: EquipmentDetail['costs'] }) {
  return (
    <Card>
      <CardHeader title="Costs" description="Spent on this equipment so far" />
      <div className="p-5">
        <DescriptionList
          columns={1}
          items={[
            { label: 'Labor hours', value: formatMinutes(costs.laborMinutes) },
            { label: 'Labor cost', value: costs.laborCost !== null ? formatMoney(costs.laborCost) : <span className="text-slate-400">No hourly rate</span> },
            { label: 'Other costs', value: formatMoney(costs.otherCosts ?? 0) },
            { label: 'Total', value: <span className="font-semibold">{formatMoney(costs.total ?? 0)}</span> },
            { label: 'Finished work orders', value: costs.finishedWorkOrders.toLocaleString() },
          ]}
        />
        {costs.hourlyRate === null && (
          <p className="mt-4 text-xs text-slate-500">Labor cost needs an hourly rate in Facilities setup, so the total leaves out labor.</p>
        )}
      </div>
    </Card>
  );
}

export function EquipmentDetailPage() {
  const { id = '' } = useParams();
  const desk = useDesk();
  const { me } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addingRecord, setAddingRecord] = useState(false);
  const [removingEntry, setRemovingEntry] = useState<EquipmentHistoryEntry | null>(null);
  const [uploading, setUploading] = useState(false);

  const query = useQuery({
    queryKey: ['facilities', 'equipment', id],
    queryFn: () => api.get<EquipmentDetail>(`/facilities/assets/${id}`),
  });

  const retire = useMutation({
    mutationFn: () => api.patch<EquipmentDetail>(`/facilities/assets/${id}`, { status: 'RETIRED' } satisfies EquipmentInput),
    onSuccess: (detail) => {
      queryClient.setQueryData(['facilities', 'equipment', id], detail);
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(`Retired ${equipmentLabel(detail)}`);
      setRetiring(false);
    },
    onError: (err) => {
      setRetiring(false);
      toast.error(errorMessage(err));
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/facilities/assets/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Equipment deleted');
      navigate('/facilities/equipment');
    },
    onError: (err) => {
      setDeleting(false);
      toast.error(errorMessage(err));
    },
  });

  const removeEntry = useMutation({
    mutationFn: (entry: EquipmentHistoryEntry) => api.delete(`/facilities/assets/${id}/history/${entry.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Record removed');
      setRemovingEntry(null);
    },
    onError: (err) => {
      setRemovingEntry(null);
      toast.error(errorMessage(err));
    },
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const equipment = query.data;
  const { permissions, history, openWorkOrders, schedules, inspections } = equipment;
  const label = equipmentLabel(equipment);
  const place = placeLabel(equipment);
  const today = toDateInput(new Date());
  const canRemoveEntry = (entry: EquipmentHistoryEntry) =>
    permissions.edit && !entry.workOrderId && (desk.can('ADMIN') || (!!me && entry.createdById === me.user.id));

  return (
    <>
      <PageHeader
        back={{ to: '/facilities/equipment', label: 'Equipment' }}
        title={label}
        meta={
          <>
            <Badge>{FACILITY_SYSTEM_LABEL[equipment.system]}</Badge>
            <ConditionBadge condition={equipment.condition} />
            <EquipmentStatusBadge status={equipment.status} />
            {place && (
              <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                <MapPin className="size-3.5" />
                {place}
              </span>
            )}
            {equipment.retiredOn && <span className="text-sm text-slate-500">Retired {formatUtcDate(equipment.retiredOn)}</span>}
          </>
        }
        actions={
          <>
            {permissions.delete && (
              <Button variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(true)}>
                Delete
              </Button>
            )}
            {permissions.edit && equipment.status !== 'RETIRED' && (
              <Button icon={<Archive />} onClick={() => setRetiring(true)}>
                Retire
              </Button>
            )}
            {permissions.edit && (
              <Button icon={<Pencil />} onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            <Button variant="primary" icon={<TriangleAlert />} onClick={() => setReporting(true)}>
              Report a problem
            </Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="History"
              description="Maintenance, repairs, inspections, and replacements over its life"
              actions={
                permissions.edit && (
                  <Button size="sm" icon={<Plus />} onClick={() => setAddingRecord(true)}>
                    Add record
                  </Button>
                )
              }
            />
            {history.length === 0 ? (
              <EmptyState icon={<History />} title="No history yet" description="Finished work orders add records here, and you can record past work by hand." />
            ) : (
              <ol className="my-5 mr-5 ml-7 space-y-6 border-l border-slate-200">
                {history.map((entry) => (
                  <li key={entry.id} className="relative pl-5">
                    <span className="absolute top-1.5 -left-[5.5px] size-2.5 rounded-full bg-brand-500 ring-4 ring-white" />
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={HISTORY_KIND_TONE[entry.kind]}>{HISTORY_KIND_LABEL[entry.kind]}</Badge>
                      <span className="text-xs text-slate-500">{formatUtcDate(entry.performedOn)}</span>
                      {canRemoveEntry(entry) && (
                        <button
                          type="button"
                          onClick={() => setRemovingEntry(entry)}
                          className="ml-auto rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                          aria-label={`Remove “${entry.summary}”`}
                          title="Remove record"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-medium text-slate-900">{entry.summary}</p>
                    {entry.details && <p className="mt-1 text-sm whitespace-pre-line text-slate-600">{entry.details}</p>}
                    <p className="mt-1 text-xs text-slate-500">
                      {[entry.cost !== null && formatMoney(entry.cost), entry.vendor, `Added by ${entry.createdByName}`].filter(Boolean).join(' · ')}
                      {entry.workOrder && (
                        <>
                          {' · '}
                          <Link to={`/facilities/work-orders/${entry.workOrder.id}`} className="text-brand-700 hover:underline">
                            Work order #{entry.workOrder.number}
                          </Link>
                        </>
                      )}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          <Card>
            <CardHeader title="Open work orders" description={openWorkOrders.length ? `${openWorkOrders.length} open` : undefined} />
            {openWorkOrders.length === 0 ? (
              <EmptyState icon={<Wrench />} title="No open work orders" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Work order</Th>
                    <Th>Status</Th>
                    <Th className="hidden sm:table-cell">Priority</Th>
                    <Th className="hidden md:table-cell">Due</Th>
                    <Th className="hidden md:table-cell">Assigned to</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {openWorkOrders.map((workOrder) => (
                    <tr key={workOrder.id}>
                      <Td className="max-w-xs">
                        <Link to={`/facilities/work-orders/${workOrder.id}`} className="block truncate font-medium text-slate-900 hover:text-brand-700">
                          <span className="text-slate-400">#{workOrder.number}</span> {workOrder.title}
                        </Link>
                      </Td>
                      <Td>
                        <WorkOrderStatusBadge status={workOrder.status} />
                      </Td>
                      <Td className="hidden sm:table-cell">
                        <PriorityBadge priority={workOrder.priority} />
                      </Td>
                      <Td className={cn('hidden whitespace-nowrap md:table-cell', isOverdue(workOrder.dueAt) && 'font-medium text-red-700')}>
                        {formatDue(workOrder.dueAt)}
                      </Td>
                      <Td className="hidden md:table-cell">{workOrder.assignedTo?.name ?? <span className="text-slate-400">Unassigned</span>}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Details" />
            <div className="p-5">
              <DescriptionList
                columns={1}
                items={[
                  { label: 'Code', value: equipment.code && <span className="font-mono">{equipment.code}</span> },
                  { label: 'Manufacturer', value: equipment.manufacturer },
                  { label: 'Model', value: equipment.model },
                  { label: 'Serial number', value: equipment.serialNumber && <span className="font-mono">{equipment.serialNumber}</span> },
                  { label: 'Vendor', value: equipment.vendor },
                  { label: 'Location detail', value: equipment.locationDetail },
                  { label: 'Notes', value: equipment.notes && <span className="whitespace-pre-line">{equipment.notes}</span> },
                ]}
              />
            </div>
          </Card>

          <LifecycleCard equipment={equipment} today={today} />
          <CostsCard costs={equipment.costs} />

          <Card>
            <CardHeader
              title="Maintenance schedules"
              actions={
                <Link to="/facilities/maintenance?tab=schedules" className="text-sm font-medium text-brand-700 hover:underline">
                  All schedules
                </Link>
              }
            />
            {schedules.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500">No schedules for this equipment</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {schedules.map((schedule) => (
                  <li key={schedule.id} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 text-sm font-medium text-slate-900">{schedule.name}</p>
                      {!schedule.enabled && <Badge>Paused</Badge>}
                    </div>
                    <p className="text-xs text-slate-500">
                      {SCHEDULE_KIND_LABEL[schedule.kind]} ·{' '}
                      {schedule.nextDueOn ? (
                        <span className={cn(schedule.enabled && schedule.nextDueOn < today && 'font-medium text-red-700')}>
                          next due {formatUtcDate(schedule.nextDueOn)}
                        </span>
                      ) : (
                        'nothing due'
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader title="Inspections" />
            {inspections.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500">No inspections of this equipment</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {inspections.map((inspection) => {
                  const open = inspection.status === 'SCHEDULED' || inspection.status === 'IN_PROGRESS';
                  return (
                    <li key={inspection.id} className="px-5 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <Link to={`/facilities/inspections/${inspection.id}`} className="min-w-0 text-sm font-medium text-slate-900 hover:text-brand-700">
                          {inspection.name}
                        </Link>
                        <InspectionStatusBadge status={inspection.status} overdue={open && !!inspection.dueOn && inspection.dueOn < today} />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        {inspection.status === 'COMPLETED' && <ScoreBadge score={inspection.score} />}
                        <span>
                          {inspection.completedAt
                            ? `Completed ${formatDate(inspection.completedAt)}`
                            : inspection.dueOn
                              ? `Due ${formatUtcDate(inspection.dueOn)}`
                              : 'No due date'}
                        </span>
                        {inspection.failedCount > 0 && <span className="text-red-700">{inspection.failedCount} failed</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Documents"
              actions={
                desk.can('TECHNICIAN') && (
                  <Button size="sm" icon={<Upload />} onClick={() => setUploading(true)}>
                    Upload
                  </Button>
                )
              }
            />
            <DocumentList documents={equipment.documents} emptyText="No manuals, warranties, or other documents yet" />
          </Card>
        </div>
      </div>

      <WorkOrderFormModal
        open={reporting}
        onClose={() => setReporting(false)}
        staff={desk.can('TECHNICIAN')}
        equipment={{ id: equipment.id, code: equipment.code, name: equipment.name, buildingId: equipment.buildingId, roomId: equipment.roomId }}
        onCreated={(t) => navigate(`/facilities/work-orders/${t.id}`)}
      />
      <EquipmentFormModal open={editing} onClose={() => setEditing(false)} equipment={equipment} />
      <HistoryEntryModal open={addingRecord} onClose={() => setAddingRecord(false)} equipment={equipment} />
      <DocumentUploadModal open={uploading} onClose={() => setUploading(false)} defaults={{ facilityAssetId: id, kind: 'MANUAL' }} />
      <ConfirmDialog
        open={retiring}
        onClose={() => setRetiring(false)}
        onConfirm={() => retire.mutate()}
        loading={retire.isPending}
        tone="primary"
        title={`Retire ${label}?`}
        description="Retired equipment is hidden from the equipment list and pickers, and its history is kept. Edit it to put it back in service."
        confirmLabel="Retire"
      />
      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title={`Delete ${label}?`}
        description="This permanently removes the equipment. Equipment with history, work orders, or inspections can’t be deleted — retire it instead."
        confirmLabel="Delete equipment"
      />
      <ConfirmDialog
        open={!!removingEntry}
        onClose={() => setRemovingEntry(null)}
        onConfirm={() => removingEntry && removeEntry.mutate(removingEntry)}
        loading={removeEntry.isPending}
        title="Remove this record?"
        description={removingEntry ? `“${removingEntry.summary}” is removed from the history of ${label}.` : ''}
        confirmLabel="Remove record"
      />
    </>
  );
}
